import { Implantation } from "../store/ducks/implantations.ducks"
import { RootState } from "../store/rootReducer"

export const UserTypologies = [
  "administrator",
  "manager",
  "collaborator",
  "customer",
] as const

export type UserTypes = (typeof UserTypologies)[number]

export const UserCompanyRightsList = [
  "can_deposit_invoices",
  "can_view_other_deposits",
  "access_accounting_buy",
  "access_accounting_sell",
  "access_accounting_deactivated",
  "access_ged_buy",
  "access_ged_sell",
  "access_accounting_plan",
  "access_invoicing",
  "access_ged_other",
  "access_writings",
  "access_bank",
  "access_bank_writings",
  "access_pdp_buy",
  "access_pdp_sell",
  "access_pdp_settings",
  "access_company_settings",
  "access_company_information",
] as const

export type UserCompanyRightType = (typeof UserCompanyRightsList)[number]

export type UserCompanyRightsState = {
  [K in UserCompanyRightType]: boolean
}

interface PermissionsByTypologies {
  change_role: "yes" | "no"
  create: "yes" | "no"
  delete: "yes" | "no"
  edit: "self" | "yes" | "no"
  edit_email: "self" | "yes" | "no"
  edit_notifications: "self" | "yes" | "no"
  grant_access: "visible" | "yes" | "no"
  revoke_access: "only_hidden" | "yes" | "no"
  grant_right: "visible" | "yes" | "no"
  revoke_right: "visible" | "yes" | "no"
}

interface UserPermissionsByType {
  administrator: PermissionsByTypologies
  manager: PermissionsByTypologies
  collaborator: PermissionsByTypologies
  customer: PermissionsByTypologies
  can_assign_typologies: UserTypes[]
  default_company_rights: UserCompanyRightsState
}

export function getUserPermissions(state: RootState) {
  const userPermissions =
    state.team.userPermissionsMatrix &&
    state.team.userPermissionsMatrix[state.user.typology as UserTypes]

  return userPermissions
}

export function getAssignableTypologies(state: RootState) {
  const userPermissions =
    state.team.userPermissionsMatrix &&
    state.team.userPermissionsMatrix[state.user.typology as UserTypes]

  return (userPermissions && userPermissions["can_assign_typologies"]) || []
}

export function getAvailableTypologiesToCreate(state: RootState) {
  if (!state.team.userPermissionsMatrix || state.user.typology === "") {
    return []
  }

  const userTypology = state.user.typology as UserTypes
  const userPermissions = state.team.userPermissionsMatrix[userTypology]

  return UserTypologies.filter(
    (typology) => userPermissions[typology].create === "yes"
  )
}

export type UserPermissions = Record<UserTypes, UserPermissionsByType>

export interface TeamMember {
  id: number
  first_name: string
  last_name: string | null
  email: string
  typology: UserTypes
  created_at: Date
  notifications_preferences: string
  companies_permissions: unknown[]
  implantations: number[]
  number_accessible_companies: number
  user_company_right: UserCompanyRightsState
}

export type UsersTeamResponse = Array<TeamMember>

export type NotificationPreferences = "weekly" | "none" | "daily"

export type RedirectionDirective =
  | "UNSET"
  | "ACCESS_DENIED"
  | "COMPANIES_LIST"
  | "SPECIFIC_COMPANY"
  | "COMPANY_CREATION"
  | "ERROR"
  | "TERMS_OF_SERVICE_NOT_ACCEPTED"
  | "FIDUCIARY_NOT_CREATED"
  | "TERMS_OF_SALES_NOT_ACCEPTED"
  | "FIRST_CONNEXION"
  | "SSO"

export function sortByLastName(
  users: TeamMember[],
  asc: boolean
): TeamMember[] {
  return [
    ...(asc
      ? users.sort((a, b) => a.last_name?.localeCompare(b.last_name || "") || 0)
      : users.sort(
          (a, b) => b.last_name?.localeCompare(a.last_name || "") || 0
        )),
  ]
}

export function sortByTypology(
  asc: boolean,
  users: TeamMember[]
): TeamMember[] {
  return [
    ...(asc
      ? users.sort((a, b) => b.typology.localeCompare(a.typology))
      : users.sort((a, b) => a.typology.localeCompare(b.typology))),
  ]
}

export function sortByEmail(asc: boolean, users: TeamMember[]): TeamMember[] {
  const filteredUsers = users.filter((user) => user.email)
  return [
    ...(asc
      ? filteredUsers.sort((a, b) => a.email.localeCompare(b.email))
      : filteredUsers.sort((a, b) => b.email.localeCompare(a.email))),
  ]
}

export function sortAlphabetically(asc: boolean, users: TeamMember[]) {
  const filterUsersByType = (type: string) =>
    Object.values(users).filter((user) => user.typology === type)

  const administrators = filterUsersByType("administrator")
  const managers = filterUsersByType("manager")
  const collaborators = filterUsersByType("collaborator")
  const customers = filterUsersByType("customer")

  const sortedAdministrators = sortByLastName(administrators, asc)
  const sortedManagers = sortByLastName(managers, asc)
  const sortedCollaborators = sortByLastName(collaborators, asc)
  const sortedCustomers = sortByLastName(customers, asc)

  return [
    ...sortedAdministrators,
    ...sortedManagers,
    ...sortedCollaborators,
    ...sortedCustomers,
  ]
}

export function searchUsers(
  users: TeamMember[],
  implantations: Implantation[],
  search: string
) {
  const lowercaseSearch = search.toLowerCase()
  if (!lowercaseSearch) return users

  const implantationsById = new Map<number, Implantation>()
  implantations.forEach((impl) => implantationsById.set(impl.id, impl))

  return users.filter((user) => {
    const firstName = user.first_name?.toLowerCase()
    const lastName = user.last_name?.toLowerCase()
    const email = user.email?.toLowerCase()

    const userMatch = [firstName, lastName, email]
      .filter(Boolean) // remove null values
      .some((userField) => userField?.includes(lowercaseSearch))

    const userAssociatedImplantations = user.implantations
      .map((id) => implantationsById.get(id))
      .filter(Boolean) as Implantation[]

    const implantationMatch = userAssociatedImplantations.some((impl) =>
      impl.name.toLowerCase().includes(lowercaseSearch)
    )

    return userMatch || implantationMatch
  })
}

export const getUpdatedRight = (
  userCompanyRights: UserCompanyRightsState,
  right: UserCompanyRightType
) => {
  const newValue = !userCompanyRights[right]
  userCompanyRights[right] = newValue

  if (right === "access_writings" && newValue) {
    userCompanyRights["access_accounting_buy"] = true
    userCompanyRights["access_accounting_sell"] = true
  }

  if (
    ["access_accounting_buy", "access_accounting_sell"].includes(right) &&
    !newValue
  ) {
    userCompanyRights["access_writings"] = false
  }

  if (right === "access_bank_writings" && newValue) {
    userCompanyRights["access_bank"] = true
  }

  if (right === "access_bank" && !newValue) {
    userCompanyRights["access_bank_writings"] = false
  }

  return userCompanyRights
}

export const haveGrantRight = (
  userPermissions: UserPermissionsByType | null,
  userCompanyRights: UserCompanyRightsState,
  typology: UserTypes,
  right: UserCompanyRightType
) => {
  if (!userPermissions) return false
  const grantRight = userPermissions[typology].grant_right
  if (grantRight === "visible") {
    return userCompanyRights[right]
  }
  return grantRight === "yes"
}

export const haveRevokeRight = (
  userPermissions: UserPermissionsByType | null,
  userCompanyRights: UserCompanyRightsState,
  typology: UserTypes,
  right: UserCompanyRightType
) => {
  if (!userPermissions) return false
  const revokeRight = userPermissions[typology].revoke_right
  if (revokeRight === "visible") {
    return userCompanyRights[right]
  }
  return revokeRight === "yes"
}
